import type { QueryClient } from '@tanstack/react-query';

import { REALTIME_RECONNECTED, REALTIME_TOPIC_REVOKED, type RealtimeManager } from '@/shared/realtime';

import { listsKeys } from './api';
import { useDeletedLists } from './model/deleted';

/** Realtime event names of the lists module (backend `ListsEvents`). */
export const LISTS_EVENTS = {
  itemChanged: 'lists.item-changed',
  listChanged: 'lists.list-changed',
  catalogChanged: 'lists.catalog-changed',
} as const;

/** `lists.item-changed` on `list:<listId>`: ids only — subscribers refetch what they may see. */
export interface ItemChangedPayload {
  listId: string;
  itemId: string;
  reason: 'created' | 'updated' | 'deleted' | 'restored' | 'comment';
}

/** `lists.list-changed` on `list:<listId>` and `lists.catalog-changed` to the users concerned. */
export interface ListChangedPayload {
  listId: string;
  reason: string;
}

/** Which cached queries an event makes stale (pure, unit tested). */
export function staleQueriesFor(
  type: string,
  payload: { listId?: string; itemId?: string; reason?: string } | null,
): (readonly unknown[])[] {
  const listId = payload?.listId;
  if (!listId) return [];
  switch (type) {
    case LISTS_EVENTS.itemChanged: {
      if (payload.reason === 'comment') return payload.itemId ? [listsKeys.comments(payload.itemId)] : [];
      const keys: (readonly unknown[])[] = [listsKeys.items(listId), listsKeys.catalogs()];
      if (payload.itemId) keys.push(listsKeys.item(payload.itemId), listsKeys.history(payload.itemId));
      return keys;
    }
    case LISTS_EVENTS.listChanged:
      // A deleted list is not asked for again (its page says it is gone); the catalogs drop it.
      if (payload.reason === 'deleted') return [listsKeys.catalogs()];
      return payload.reason === 'views'
        ? [listsKeys.views(listId)]
        : [
            listsKeys.list(listId),
            listsKeys.items(listId),
            listsKeys.catalogs(),
            ['lists', 'item'],
            listsKeys.permissions(listId),
          ];
    case LISTS_EVENTS.catalogChanged:
      // The processes of the feed's composer and the gallery of templates follow the catalog (a list was installed or deleted).
      return payload.reason === 'deleted'
        ? [listsKeys.catalogs(), listsKeys.feedProcesses(), listsKeys.templates()]
        : [listsKeys.catalogs(), listsKeys.list(listId), listsKeys.feedProcesses(), listsKeys.templates()];
    default:
      return [];
  }
}

/**
 * Keeps the lists caches live: `lists.*` events (the topic `list:<id>` of an open table or card, and catalog changes
 * sent to the user) invalidate what they affect; a revoked topic drops the list the user may no longer see; a
 * reconnect refetches everything.
 */
export function registerListsRealtime(realtime: RealtimeManager, queryClient: QueryClient): () => void {
  const offEvents = realtime.on<{ listId?: string; itemId?: string; reason?: string } | null>(
    'lists.*',
    (payload, envelope) => {
      // A deleted list (not item): its page says so instead of asking the server again.
      const listGone =
        envelope.type === LISTS_EVENTS.listChanged || envelope.type === LISTS_EVENTS.catalogChanged;
      if (listGone && payload?.reason === 'deleted' && payload.listId) {
        useDeletedLists.getState().add(payload.listId);
      }
      for (const queryKey of staleQueriesFor(envelope.type, payload)) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
  );
  const offRevoked = realtime.on<{ topic?: string } | null>(REALTIME_TOPIC_REVOKED, (payload) => {
    const topic = payload?.topic;
    if (!topic?.startsWith('list:')) return;
    const listId = topic.slice('list:'.length);
    void queryClient.resetQueries({ queryKey: listsKeys.list(listId) });
    void queryClient.resetQueries({ queryKey: listsKeys.items(listId) });
    void queryClient.invalidateQueries({ queryKey: listsKeys.catalogs() });
  });
  const offReconnect = realtime.on(REALTIME_RECONNECTED, () => {
    void queryClient.invalidateQueries({ queryKey: listsKeys.all });
  });
  return () => {
    offEvents();
    offRevoked();
    offReconnect();
  };
}
